import {
  Property,
  Unit,
  Verification,
  Address,
  Money,
  ApplicationStatus,
  LeadContactMethod
} from "shared/types";
import { Stage } from "views/settings-crm/types/stage";
import type { ContactFormFieldType } from "views/settings-crm/slices/contact-forms/types";
import { NoteNotification } from "shared/app/Notes/types";
import { ApprovalLikelihoodBand } from "api/approval-likelihood";

type ChangeValue = string | number | null;

interface LeadActivityChange {
  from?: ChangeValue;
  to?: ChangeValue;
  from_display?: ChangeValue;
  to_display?: ChangeValue;
  label?: string;
}
import { User } from "views/settings-users/types";

export enum LeadTab {
  DETAILS = "details",
  FILES = "files",
  PRE_SCREENING = "pre-screening",
  TASKS = "tasks"
}

export enum LeadType {
  Tenant = "tenant",
  Owner = "owner"
}

export const LEAD_TYPE_LABEL: Record<LeadType, string> = {
  [LeadType.Tenant]: "Tenant",
  [LeadType.Owner]: "Owner"
};

export const LEAD_TYPE_OPTIONS = [
  { label: LEAD_TYPE_LABEL[LeadType.Tenant], value: LeadType.Tenant },
  { label: LEAD_TYPE_LABEL[LeadType.Owner], value: LeadType.Owner }
];

export type ContactMethod = LeadContactMethod & {
  id: number;
  sub_type: string | null;
  created_at: string;
  updated_at: string;
};

export interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  emails: string[];
  phones: string[];
  contact_methods: ContactMethod[];
  created_at: string;
  updated_at: string;
  fraudulent?: boolean;
  marked_fraudulent_at?: string | null;
  marked_fraudulent_by?: {
    id: number;
    name: string;
    email: string;
  } | null;
}

export interface LeadSource {
  can_delete: boolean;
  can_edit: boolean;
  color: string | null;
  created_at: string;
  display_order: number;
  enable_contact_form: boolean;
  id: number;
  is_active: boolean;
  lead_ingest_email: string | null;
  name: string;
  source_type: string;
  updated_at: string;
}

export enum LeadActivityType {
  LEAD_CREATED_BY_USER = "lead_created_by_user",
  LEAD_CREATED_AUTO = "lead_created_auto",
  LEAD_DETAILS_EDITED = "lead_details_edited",
  FILE_UPLOADED = "file_uploaded",
  FILE_DELETED = "file_deleted",
  NOTE_ADDED = "note_added",
  EMAIL_INVITE_SENT = "email_invite_sent",
  STAGE_CHANGED_MANUAL = "stage_changed_manual",
  STAGE_CHANGED_AUTO = "stage_changed_auto",
  IDENTITY_VERIFICATION_PASSED = "identity_verification_passed",
  IDENTITY_VERIFICATION_PASSED_WITH_WARNINGS = "identity_verification_passed_with_warnings",
  IDENTITY_VERIFICATION_FAILED = "identity_verification_failed",
  IDENTITY_VERIFICATION_OVERRIDDEN = "identity_verification_overridden",
  IDENTITY_VERIFICATION_EXPIRED = "identity_verification_expired",
  LEAD_ASSIGNED = "lead_assigned",
  IDENTITY_VERIFICATION_DATA_EDITED = "identity_verification_data_edited",
  PRESCREENING_SUBMITTED = "prescreening_submitted",
  PRESCREENING_FAILED = "prescreening_failed",
  PRESCREENING_COMPLETED = "prescreening_completed",
  PRESCREENING_ABANDONED = "prescreening_abandoned",
  EMAIL_RECEIVED = "email_received",
  EMAIL_SENT = "email_sent",
  SMS_RECEIVED = "sms_received",
  SMS_SENT = "sms_sent",
  SMS_NOT_SENT = "sms_not_sent",
  MANUAL_RECORD = "manual_record",
  SHOWING_INVITE_SENT = "showing_invite_sent",
  SHOWING_REQUESTED = "showing_requested",
  SHOWING_SCHEDULED = "showing_scheduled",
  SHOWING_CONFIRMED = "showing_confirmed",
  AI_CALL_RECEIVED = "ai_call_received",
  OUTBOUND_CALL_PLACED = "outbound_call_placed",
  INBOUND_CALL_RECEIVED = "inbound_call_received",
  // CRM-1524: incoming calls on a plan without calling in Boom.
  INBOUND_CALL_FORWARDED = "inbound_call_forwarded",
  INBOUND_CALL_FORWARD_FAILED = "inbound_call_forward_failed",
  INBOUND_CALL_TEXT_ONLY = "inbound_call_text_only",
  SHOWING_RESCHEDULED = "showing_rescheduled",
  SHOWING_CHECKED_IN = "showing_checked_in",
  SHOWING_CHECKED_OUT = "showing_checked_out",
  SHOWING_FEEDBACK_SUBMITTED = "showing_feedback_submitted",
  SHOWING_LOCKBOX_NOT_CONFIGURED = "showing_lockbox_not_configured",
  SHOWING_CANCELLED = "showing_cancelled",
  SHOWING_REASSIGNED = "showing_reassigned",
  SHOWING_COMPLETED = "showing_completed",
  SHOWING_NO_SHOW = "showing_no_show",
  SHOWING_GUIDED_CONFIRMED = "showing_guided_confirmed",
  SHOWING_SERIAL_NUMBER_INCORRECT = "showing_serial_number_incorrect",
  SHOWING_SERIAL_NUMBER_LOCKOUT = "showing_serial_number_lockout",
  TENANT_NOTIFIED_OF_SHOWING = "tenant_notified_of_showing",
  PUSH_LEAD_TO_PMS_SUCCESS = "push_lead_to_pms_success",
  PUSH_LEAD_TO_PMS_FAILED = "push_lead_to_pms_failed",
  SHOWING_NOT_CHECKED_IN = "showing_not_checked_in"
}

export enum LeadActivitySource {
  ACTIVITY = "activity",
  APPLICATION_NOTE = "application_note"
}

export interface LeadActivity {
  id: number;
  source: LeadActivitySource;
  activity_type: LeadActivityType;
  title: string;
  description: string | null;
  actor_type: string;
  actor_name: string;
  actor?: {
    name: string;
    role: string;
  };
  metadata: {
    trigger?: string | null;
    to_stage?: Stage;
    from_stage?: Stage;
    files?: { name: string }[];
    source_name?: string;
    note_id?: number;
    desired_move_in_date?: string;
    overridden_by?: string;
    property_name?: string;
    unit_name?: string;
    partner_user_name?: string;
    start_time?: string;
    end_time?: string;
    cancelled_by?: string;
    cancellation_reason?: string;
    preview?: string;
    subject?: string;
    source?: string;
    html?: string;
    channel?: string;
    direction?: string;
    conversation_id?: string;
    message_id?: string;
    contact_id?: number;
    contact_name?: string;
    message?: string;
    note_content?: string;
    changes?: Record<string, LeadActivityChange>;
    agent_name?: string | null;
    agent_phone?: string | null;
    duration_seconds?: number | null;
    call_transcript_id?: number | null;
    call_id?: number;
    to_number?: string;
    from_number?: string;
    status?: string;
    // Missed-call fields on inbound call activities (CRM-1235). `missed` is the
    // canonical server-side verdict; the other three are carried so the timeline
    // can render (and older rows can be classified) without a second request.
    missed?: boolean | null;
    answered_at?: string | null;
    answered_by_partner_user_id?: number | null;
    ring_seconds?: number | null;
    recording_url?: string | null;
    lockbox_code?: string;
    checked_in_at?: string;
    checked_out_at?: string;
    showing_id?: number;
    attempt_number?: number;
    old_start_time?: string;
    new_start_time?: string;
    decline_reasons?: string[];
    requested_by?: string;
    timezone?: string;
    pms_type?: string;
    pms_id?: string;
    error_message?: string;
    reason?: string;
    notification?: string;
    tenant_name?: string;
    recipient?: string;
  };
  rich_text?: {
    document_html: string;
    document_text: string;
    notifications?: NoteNotification[];
  } | null;
  occurred_at: string;
  created_at: string;
  updated_at: string;
}

// CRM-1235. Whether an inbound-call activity is a missed call. The verdict is
// decided server-side and shipped as `metadata.missed`; the fallback classifies
// rows logged before that field existed, where the terminal status plus the
// absent answering user are all the activity carries.
export const isMissedInboundCallActivity = (
  activityType: string,
  metadata?: LeadActivity["metadata"]
): boolean => {
  if (activityType !== LeadActivityType.INBOUND_CALL_RECEIVED) return false;
  if (typeof metadata?.missed === "boolean") return metadata.missed;
  return (
    metadata?.status !== "completed" &&
    metadata?.answered_by_partner_user_id == null
  );
};

export interface LeadApplicant {
  id: number;
  role: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  email?: string;
}

export interface LeadApplication {
  id: number;
  unit: Unit;
  monthly_rent_amount: Money;
  property: Property;
  status: ApplicationStatus;
  screening_status: string;
  desired_move_in_date?: string;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  started_at?: string;
  conditions: unknown[];
  include_co_signer_in_criteria_calculation: boolean;
}

// Answers to partner-invented custom fields on an embeddable contact form (CRM-982).
// The label is snapshotted server-side, so it stays readable after the form is edited.
export interface LeadCustomFieldResponse {
  id: number;
  label: string;
  value: string | null;
  // Both null once the contact-form field behind the answer has been deleted — the FK
  // is ON DELETE SET NULL, and the answer outlives its field.
  field_type: ContactFormFieldType | null;
  options: string[] | null;
}

export enum FraudIndicatorType {
  FailedPrescreeningSamePartner = "failed_prescreening_same_partner",
  IpDistanceFromProperty = "ip_distance_from_property",
  ShowingsSpread = "showings_spread",
  RapidFireShowings = "rapid_fire_showings",
  FlaggedFraudulentOtherPartner = "flagged_fraudulent_other_partner",
  IpqsHighFraudScore = "ipqs_high_fraud_score",
  IpqsDisposableEmail = "ipqs_disposable_email",
  IpqsInvalidContact = "ipqs_invalid_contact",
  IpqsRecentAbuse = "ipqs_recent_abuse",
  IpqsLeaked = "ipqs_leaked",
  IpqsSpammerOrDnc = "ipqs_spammer_or_dnc",
  IpqsVoipPhone = "ipqs_voip_phone"
}

export const NON_PUNITIVE_FRAUD_INDICATOR_TYPES: FraudIndicatorType[] = [
  FraudIndicatorType.IpDistanceFromProperty,
  FraudIndicatorType.IpqsVoipPhone
];

export interface FraudIndicator {
  type: FraudIndicatorType;
  detected_at: string;
  data: {
    // failed_prescreening_same_partner
    failed_on?: string;
    property_name?: string | null;
    unit_name?: string | null;
    // ip_distance_from_property
    distance_miles?: number;
    property_address?: string;
    // showings_spread
    locations?: { city: string | null; state: string | null }[];
    max_distance_miles?: number;
    // rapid_fire_showings
    showing_count?: number;
    window_minutes?: number;
    requested_on?: string;
    // flagged_fraudulent_other_partner
    flagged_on?: string;
    // ipqs_high_fraud_score
    email?: number;
    phone?: number;
    // ipqs_invalid_contact, ipqs_recent_abuse, ipqs_leaked
    channels?: ("email" | "phone")[];
    // ipqs_spammer_or_dnc
    spammer?: boolean;
    do_not_call?: boolean;
  };
}

export interface Lead {
  id: number;
  first_name: string;
  middle_name?: string;
  last_name: string;
  status: string;
  lead_type: LeadType;
  portfolio_size?: number | null;
  current_property_manager?: string | null;
  starred: boolean;
  created_at: string;
  updated_at: string;
  primary_email: string;
  primary_phone: string | null;
  address: Address;
  address_id: number;
  applicant_id: number;
  application_id: number;
  approval_likelihood_band?: ApprovalLikelihoodBand | null;
  approval_likelihood_insufficient_data?: boolean;
  desired_move_in_date: string;
  contact_methods: ContactMethod[];
  property_id: number;
  unit_id: number;
  from_waitlist: boolean;
  message: string;
  custom_field_responses?: LeadCustomFieldResponse[];
  all_emails: string[];
  all_phones: string[];
  property?: Property;
  unit?: Unit;
  applicant?: LeadApplicant;
  application?: LeadApplication;
  application_status_for_lead?: ApplicationStatus | null;
  requires_overrides_identity_verification?: boolean;
  verifications: Verification[];
  source?: LeadSource;
  current_stage_id?: number;
  current_stage?: {
    id: number;
    color: string;
    is_fixed: boolean;
    name: string;
    position: string;
  };
  contact?: Contact;
  recent_activity_summary?: {
    activity_type: LeadActivityType;
    actor_name: string;
    occurred_at: string;
    title: string;
  };
  recent_activities?: {
    id: number;
    activity_type: LeadActivityType;
    actor_name: string | null;
    occurred_at: string;
    title: string;
  }[];
  last_note?: {
    rich_text?: {
      document_html: string;
      document_text: string;
    } | null;
    occurred_at: string;
    actor_name: string;
    source_name?: string | null;
  } | null;
  assigned_to: string;
  assigned_to_user?: User | null;
  assignees?: { id: number; name: string; email: string; role?: string }[];
  stage: string;
  created_by: User | null;
  invited_by?: { id: number; name: string } | null;
  fraud_indicators?: FraudIndicator[];
  fraud_indicator_count?: number;
}
